import { Component, ElementRef, ViewChild } from "@angular/core";
import { ActiveStatus, FeatureChoice, Feature, ProductTypeTag, AddonStation } from "app/model/entities/entity-model";
import { DialogBaseComponent, DialogParams } from "app/shared/dialog-base.component";
import { DomainService } from "app/shared/domain.service";
import { UtilFns } from "app/shared/util-fns";
import { Entity } from "breeze-client";
import { DynamicDialogConfig, DynamicDialogRef } from "primeng/dynamicdialog";

export interface GenericEntityDialogParams extends DialogParams {
  target?: Entity & { id: any };
  targetTypeDescription: string;
  targetType: new () => Entity & { id: any };
  fieldNames: string[];
  fieldLabels: string[];
  altKeyFieldNames: string[];
  newEntityStruct?: object;
  beforeSaveFn?: (entity: Entity, isAdding: boolean) => void;
}

export const ProductTypeTagDialogParams: GenericEntityDialogParams = {
  targetTypeDescription: "Product Type Tag",
  targetType: ProductTypeTag,
  fieldNames: ["name"],
  fieldLabels: ["Name"],
  altKeyFieldNames: ["name"],
};

export const AddonStationDialogParams: GenericEntityDialogParams = {
  targetTypeDescription: "Addon Station",
  targetType: AddonStation,
  fieldNames: ["name"],
  fieldLabels: ["Name"],
  altKeyFieldNames: ["name"],
};

export const FeatureDialogParams = {
  targetTypeDescription: "Feature",
  targetType: Feature,
  fieldNames: ["name"],
  fieldLabels: ["Name"],
  altKeyFieldNames: ["name"],
  beforeSaveFn: (entity: Entity, isAdding) => {
    if (!isAdding) return;
    const f = entity as Feature;
    f._origName = f.name;
  },
};

export const FeatureChoiceDialogParams = {
  targetTypeDescription: "Feature Choice",
  targetType: FeatureChoice,
  fieldNames: ["choiceValue"],
  fieldLabels: ["Value"],
  altKeyFieldNames: ["choiceValue", "featureId"],
};

@Component({
  templateUrl: "./edit-generic-entity-dialog.component.html",
  standalone: false,
})
// NOTE: cannot inherit from DialogBaseComponent because this generate a circular dependency of some kind that
// I haven't been able to resolve... so I copied some of the code from DialogBaseComponent here.
export class EditGenericEntityDialogComponent extends DialogBaseComponent<GenericEntityDialogParams> {
  @ViewChild("firstInput") firstInput: ElementRef<HTMLInputElement>;

  target?: Entity & { id: any };
  targetTypeDescription: string;
  targetType: new () => Entity & { id: any };
  fieldNames: string[];
  fieldLabels: string[];
  altKeyFieldNames: string[];
  newEntityStruct?: object;
  beforeSaveFn?: (entity: Entity, isAdding: boolean) => void;

  isAdding: boolean;

  activeStatuses: ActiveStatus[];

  constructor(
    public config: DynamicDialogConfig<GenericEntityDialogParams>,
    public dialogRef: DynamicDialogRef,
    public domainService: DomainService
  ) {
    super(config, dialogRef, domainService);
    this.prepare();
  }

  private async prepare() {
    await UtilFns.wait(1);

    Object.assign(this, this.config.data);

    if (this.target == null) {
      this.isAdding = true;
      this.target = this.createTargetAsUnchanged();
    }

    this.activeStatuses = this.dbQueryService.getAllCached(ActiveStatus);

    this.config.header = this.isAdding ? `Add ${this.targetTypeDescription}` : `Edit ${this.targetTypeDescription}`;

    this.isPageReady = true;

    // Focus the first input after the view renders
    await UtilFns.wait(1);
    this.firstInput?.nativeElement?.focus();
  }

  private createTargetAsUnchanged() {
    let struct = {
      activeStatusId: 1,
    };
    if (this.newEntityStruct) {
      struct = { ...struct, ...this.newEntityStruct };
    }
    const entity = this.dbQueryService.uow.createEntity(this.targetType, struct as any);
    entity.entityAspect.setUnchanged();
    return entity;
  }

  async ok() {
    const ok = await this.save();
    if (!ok) {
      return;
    }
    this.dialogRef.close(true);
  }

  async save() {
    if (this.isAdding) {
      this.target.entityAspect.setAdded();
    }
    // TODO: remove excess spaces
    if (this.beforeSaveFn) {
      this.beforeSaveFn(this.target, this.isAdding);
    }
    const isValid = await this.validate();
    if (!isValid) {
      return false;
    }

    try {
      const sr = await this.dbSaveService.saveSelectedChanges([this.target]);
      return true;
    } catch (e) {
      return false;
    }
  }

  async validate() {
    let matchingEntity = await this.dbQueryService.getMatchingEntity(this.targetType, this.target, this.altKeyFieldNames);
    if (matchingEntity != null) {
      await this.dialogService.showOkMessage(
        "Duplicate",
        `Cannot save because this ${this.targetTypeDescription} is a duplicate of an existing ${this.targetTypeDescription}. `
      );
      return false;
    }

    const ea = this.target.entityAspect;
    if (!ea.validateEntity()) {
      const vErrors = ea.getValidationErrors();
      const errMsg = vErrors.map((ve) => ve.errorMessage).join("<br>");
      await this.dialogService.showOkMessage("Validation Error(s)", errMsg);
      return false;
    }

    return true;
  }

  async cancel() {
    // This insures that the rejectChanges below does not cause an html error on the page.
    this.isPageReady = false;
    // entityAspect may not exist if it is a new object
    this.target.entityAspect?.rejectChanges();

    this.dialogRef.close(false);
  }
}
